import React, { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { WheelEntry, WheelEntryType } from '../../types/wheel';

interface WheelEntryEditorProps {
  entries: WheelEntry[];
  onChange: (entries: WheelEntry[]) => void;
}

// Findom-relevant emojis
const findomEmojis = ['💰', '💸', '💵', '💳', '👑', '🔒', '💎', '🛍️', '💋', '🔥'];

/**
 * Component for adding, editing, and removing wheel entries
 */
const WheelEntryEditor: React.FC<WheelEntryEditorProps> = ({ entries, onChange }) => {
  const [newEntryText, setNewEntryText] = useState<string>('');
  const [selectedEmoji, setSelectedEmoji] = useState<string | null>(null);

  /**
   * Add a new entry to the wheel
   */
  const handleAddEntry = () => {
    if (!newEntryText.trim()) return;

    const newEntry: WheelEntry = {
      id: uuidv4(),
      text: newEntryText.trim(),
      type: WheelEntryType.TEXT,
      weight: 1, // All entries have equal weight as per requirements
      emoji: selectedEmoji || undefined,
    };

    onChange([...entries, newEntry]);
    setNewEntryText('');
    setSelectedEmoji(null);
  };

  /**
   * Handle Enter key press to add entry
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEntry();
    }
  };

  /**
   * Delete an entry from the wheel
   */
  const handleDeleteEntry = (id: string | undefined) => {
    if (!id) return;
    onChange(entries.filter(entry => entry.id !== id));
  };

  /**
   * Move an entry up or down in the list
   */
  const handleMoveEntry = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === entries.length - 1)
    ) {
      return; // Can't move past the boundaries
    }

    const newEntries = [...entries];
    if (direction === 'up') {
      // Swap with the previous entry
      [newEntries[index - 1], newEntries[index]] = [newEntries[index], newEntries[index - 1]];
    } else {
      // Swap with the next entry
      [newEntries[index], newEntries[index + 1]] = [newEntries[index + 1], newEntries[index]];
    }

    onChange(newEntries);
  };

  return (
    <div className="wheel-entry-editor">
      {/* Add new entry */}
      <div className="mb-4">
        <label htmlFor="newEntry" className="block text-gray-700 font-medium mb-2">
          Add New Entry
        </label>
        <div className="flex flex-col">
          <div className="flex">
            <input
              type="text"
              id="newEntry"
              className="flex-1 px-4 py-2 border rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Type a new entry and press Enter"
              value={newEntryText}
              onChange={e => setNewEntryText(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            <button
              type="button"
              className="px-4 py-2 bg-primary text-white rounded-r-lg hover:bg-primary-dark transition-colors"
              onClick={handleAddEntry}
              disabled={!newEntryText.trim()}
            >
              Add
            </button>
          </div>
          {/* Emoji Picker */}
          <div className="mt-2 p-2 border rounded-lg bg-white shadow-sm">
            <p className="text-sm text-gray-600 mb-2">Select an emoji (optional):</p>
            <div className="flex flex-wrap gap-2">
              {findomEmojis.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  className={`w-10 h-10 text-xl flex items-center justify-center rounded-md hover:bg-gray-100 transition-colors ${
                    selectedEmoji === emoji ? 'bg-primary/10 border border-primary' : ''
                  }`}
                  onClick={() => setSelectedEmoji(selectedEmoji === emoji ? null : emoji)}
                  aria-label={`Select emoji ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
            {selectedEmoji && (
              <div className="mt-2 flex items-center">
                <span className="text-sm text-gray-600 mr-2">Selected:</span>
                <span className="text-xl">{selectedEmoji}</span>
                <button
                  type="button"
                  className="ml-auto text-xs text-red-500 hover:text-red-700"
                  onClick={() => setSelectedEmoji(null)}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Entries list */}
      <div className="mt-4">
        <h3 className="font-medium text-gray-700 mb-2">Current Entries ({entries.length})</h3>
        {entries.length === 0 ? (
          <div className="text-gray-500 p-4 bg-gray-100 rounded-lg text-center">
            No entries yet. Add some above!
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {entries.map((entry, index) => (
              <div
                key={entry.id}
                className="flex items-center justify-between p-3 border-b hover:bg-gray-50 group"
              >
                <span>
                  {entry.emoji && <span className="mr-2">{entry.emoji}</span>}
                  {entry.text}
                </span>
                <div className="flex items-center">
                  {/* Reorder buttons */}
                  <div className="flex mr-2">
                    <button
                      type="button"
                      className="text-gray-500 hover:text-primary p-1 disabled:opacity-30"
                      onClick={() => handleMoveEntry(index, 'up')}
                      disabled={index === 0}
                      aria-label="Move entry up"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="text-gray-500 hover:text-primary p-1 disabled:opacity-30"
                      onClick={() => handleMoveEntry(index, 'down')}
                      disabled={index === entries.length - 1}
                      aria-label="Move entry down"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                  {/* Delete button */}
                  <button
                    type="button"
                    className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleDeleteEntry(entry.id)}
                    aria-label="Delete entry"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WheelEntryEditor;
